import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, ShoppingBag, Trash2, Plus, Edit3, Save, X, Image as ImageIcon, Loader2, Upload } from "lucide-react";

const CATEGORIES = ["reel-cover", "story", "quote", "festival", "business", "fashion"];
const ASPECTS = ["9:16", "1:1", "4:5", "16:9"];

export default function TemplatesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template or null
  const [form, setForm] = useState(initialForm());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgRef = useRef(null);

  const uploadImage = async (file, field) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setMsg("Pick an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { setMsg("Image must be under 8MB"); return; }
    setUploadingImg(true); setMsg("");
    try {
      const { uploadToStorage } = await import("../lib/uploads");
      const cu = await uploadToStorage(file, "templates");
      setForm(f => ({ ...f, [field]: cu.secure_url }));
      setMsg("✓ Image uploaded");
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg("Image upload failed. " + (e?.message || ""));
    } finally { setUploadingImg(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  function initialForm() {
    return { name: "", category: "reel-cover", description: "", preview_url: "", download_url: "", price_goldly: 50, aspect_ratio: "9:16", tier: "standard" };
  }

  const load = () => {
    setLoading(true);
    api.get("/templates").then(r => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name, category: t.category, description: t.description || "",
      preview_url: t.preview_url, download_url: t.download_url || "",
      price_goldly: t.price_goldly, aspect_ratio: t.aspect_ratio || "9:16",
      tier: t.tier || "standard",
    });
    setMsg("");
  };

  const reset = () => { setEditing(null); setForm(initialForm()); setMsg(""); };

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setMsg("");
    const payload = { ...form, price_goldly: parseInt(form.price_goldly) || 0 };
    try {
      if (editing) {
        await api.patch(`/admin/templates/${editing.id}`, payload);
        setMsg("✓ Updated");
      } else {
        await api.post("/admin/templates", payload);
        setMsg("✓ Created");
      }
      load();
      setTimeout(() => { setMsg(""); reset(); }, 1200);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/templates/${t.id}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="px-4 py-6 pb-24" data-testid="templates-admin-page">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/dashboard" data-testid="ta-back" className="p-2 rounded-full bg-white/5"><ArrowLeft className="w-4 h-4"/></Link>
        <h1 className="font-outfit font-extrabold text-2xl flex items-center gap-2">
          <ShoppingBag className="w-6 h-6 text-[#A855F7]"/>Templates Store
        </h1>
      </div>

      {/* Add / Edit form */}
      <form onSubmit={save} className="rounded-2xl bg-[#A855F7]/8 border border-[#A855F7]/30 p-3 space-y-2 mb-4" data-testid="ta-form">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm flex items-center gap-1.5">
            {editing ? <><Edit3 className="w-4 h-4 text-[#A855F7]"/>Edit Template</> : <><Plus className="w-4 h-4 text-[#A855F7]"/>Add New Template</>}
          </h3>
          {editing && <button type="button" onClick={reset} data-testid="ta-cancel" className="p-1.5 rounded hover:bg-white/10"><X className="w-4 h-4"/></button>}
        </div>
        <input data-testid="ta-name" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Template name"
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="ta-category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="ta-aspect" value={form.aspect_ratio} onChange={e => setForm(f => ({ ...f, aspect_ratio: e.target.value }))}
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm">
            {ASPECTS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <textarea data-testid="ta-desc" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description"
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        <input data-testid="ta-preview" required value={form.preview_url} onChange={e => setForm(f => ({ ...f, preview_url: e.target.value }))} placeholder="Preview image URL (or upload below)"
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>

        {/* Image upload (Cloudinary) — fills the preview_url automatically */}
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => imgRef.current?.click()} disabled={uploadingImg}
            data-testid="ta-upload-img"
            className="flex-1 py-2 rounded-xl bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] font-bold text-xs flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50">
            {uploadingImg ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}
            {uploadingImg ? "Uploading…" : (form.preview_url ? "Replace preview image" : "Upload preview image")}
          </button>
          {form.preview_url && (
            <img src={form.preview_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-white/10" data-testid="ta-preview-thumb"/>
          )}
          <input ref={imgRef} type="file" accept="image/*" onChange={e => uploadImage(e.target.files?.[0], "preview_url")} className="hidden" data-testid="ta-img-input"/>
        </div>

        <input data-testid="ta-download" value={form.download_url} onChange={e => setForm(f => ({ ...f, download_url: e.target.value }))} placeholder="Download URL (optional, defaults to preview)"
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="ta-price" type="number" min={0} value={form.price_goldly} onChange={e => setForm(f => ({ ...f, price_goldly: e.target.value }))} placeholder="Price (Goldly)"
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <select data-testid="ta-tier" value={form.tier} onChange={e => setForm(f => ({ ...f, tier: e.target.value }))}
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm">
            <option value="standard">standard</option>
            <option value="premium">premium</option>
          </select>
        </div>
        {msg && <div className={`text-xs ${msg.startsWith("✓") ? "text-emerald-300" : "text-[#FF2A5F]"}`} data-testid="ta-msg">{msg}</div>}
        <button type="submit" disabled={busy} data-testid="ta-save"
          className="w-full py-2.5 rounded-full bg-[#A855F7] text-white font-extrabold disabled:opacity-50 active:scale-95 flex items-center justify-center gap-1.5">
          <Save className="w-4 h-4"/>{busy ? "Saving…" : editing ? "Save Changes" : "Add Template"}
        </button>
      </form>

      {/* List */}
      <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">All Templates ({items.length})</h3>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#A855F7]"/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-white/40 text-xs">No templates yet — add one above.</div>
      ) : (
        <div className="space-y-2" data-testid="ta-list">
          {items.map(t => (
            <div key={t.id} data-testid={`ta-row-${t.id}`} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 border border-white/10">
              {t.preview_url ? (
                <img src={t.preview_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0"/>
              ) : (
                <div className="w-14 h-14 rounded-lg bg-white/10 flex-shrink-0 flex items-center justify-center"><ImageIcon className="w-5 h-5 text-white/40"/></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{t.name}</div>
                <div className="text-[10px] text-white/50 flex items-center gap-1.5">
                  <span className="px-1.5 py-0.5 rounded bg-[#A855F7]/15 text-[#A855F7]">{t.category}</span>
                  <span>{t.aspect_ratio}</span>
                  <span className="text-[#FF7A00]">{t.price_goldly} 🪙</span>
                  {t.tier === "premium" && <span className="text-[#FF7A00]">★</span>}
                </div>
                <div className="text-[10px] text-white/40">{t.purchases_count || 0} purchases</div>
              </div>
              <button onClick={() => startEdit(t)} data-testid={`ta-edit-${t.id}`} className="p-2 rounded-full bg-white/5 hover:bg-white/10"><Edit3 className="w-4 h-4 text-white/70"/></button>
              <button onClick={() => del(t)} data-testid={`ta-del-${t.id}`} className="p-2 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F]"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
